import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePromo } from "../../hooks/usePromo";

/**
 * PromoBanner — generic, content-driven announcement bar for the active offer.
 * Renders whatever the admin set in Settings → Promo (via GET /api/promo); no
 * code change per campaign. Shows only on marketing/auth surfaces, dismissible
 * per-code (localStorage). Clicking carries ?promo=CODE to pricing so checkout
 * auto-applies the Polar discount.
 */
const PROMO_PATHS = new Set([
  "/",
  "/pricing",
  "/login",
  "/signup",
  "/forgot",
  "/reset-password",
  "/what-is-linklane",
  "/about",
  "/developers",
  "/customers",
  "/security",
  "/contact",
]);

function dismissKey(code) {
  return `ll_promo_dismissed_${(code || "").toLowerCase()}`;
}

export default function PromoBanner({ forceShow = false }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { promo } = usePromo();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return promo?.code ? localStorage.getItem(dismissKey(promo.code)) === "1" : false;
    } catch {
      return false;
    }
  });

  if (!promo || !promo.enabled) return null;
  // forceShow: rendered inside the app shell (covers all dashboard pages).
  // Otherwise gate to the marketing/auth allowlist (App.js global mount).
  if (!forceShow && !PROMO_PATHS.has(pathname)) return null;
  if (dismissed) return null;

  const onClaim = () => navigate(`/pricing?promo=${encodeURIComponent(promo.code)}`);
  const onClose = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(dismissKey(promo.code), "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const headline = promo.headline || `${promo.percent || ""}% off — limited time`;

  return (
    <div
      data-testid="promo-banner"
      onClick={onClaim}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" ? onClaim() : null)}
      className="relative z-[60] cursor-pointer bg-candy text-white"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-3 px-10 py-2.5 text-center text-sm font-semibold">
        <span className="hidden rounded-full bg-yellow px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider text-ink sm:inline-block">
          Offer
        </span>
        <span className="truncate">{headline}</span>
        <span className="inline-flex items-center gap-1 rounded-full border border-white/40 bg-white/15 px-2.5 py-0.5 font-mono text-[12px] tracking-wider">
          {promo.code}
        </span>
        <span className="hidden font-bold underline underline-offset-2 md:inline">Claim →</span>
      </div>
      <button
        type="button"
        aria-label="Dismiss offer"
        data-testid="promo-banner-dismiss"
        onClick={onClose}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full px-2 text-lg leading-none text-white/80 hover:text-white"
      >
        ×
      </button>
    </div>
  );
}
